import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function RestaurantReviews() {
  const [pendingRestaurants, setPendingRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchPendingRestaurants();
  }, []);

  const fetchPendingRestaurants = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPendingRestaurantReviews();
      setPendingRestaurants(response.data.data.restaurants);
    } catch (error) {
      setError('Failed to fetch pending restaurants');
      console.error('Error fetching pending restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRestaurantDetails = async (restaurantId) => {
    try {
      setLoading(true);
      const response = await adminAPI.getRestaurantDetailsForReview(restaurantId);
      setSelectedRestaurant(response.data.data.restaurant);
      setRejectionReason('');
    } catch (error) {
      setError('Failed to load restaurant details');
      console.error('Error loading restaurant details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDocument = async (documentType, verified) => {
    if (!selectedRestaurant) return;

    try {
      await adminAPI.verifyDocument(selectedRestaurant._id, documentType, verified);

      // Update local state
      setSelectedRestaurant({
        ...selectedRestaurant,
        documents: {
          ...selectedRestaurant.documents,
          [documentType]: {
            ...selectedRestaurant.documents[documentType],
            verified
          }
        }
      });
    } catch (error) {
      alert('Failed to verify document');
      console.error('Error verifying document:', error);
    }
  };

  const handleReviewRestaurant = async (approved) => {
    if (!selectedRestaurant) return;

    if (!approved && !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    if (!window.confirm(`Are you sure you want to ${approved ? 'APPROVE' : 'REJECT'} this restaurant?`)) {
      return;
    }

    try {
      setLoading(true);
      await adminAPI.reviewRestaurant(selectedRestaurant._id, approved, rejectionReason);

      alert(`Restaurant ${approved ? 'approved' : 'rejected'} successfully!`);

      // Refresh the lists
      await fetchPendingRestaurants();
      setSelectedRestaurant(null);
      setRejectionReason('');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to review restaurant');
      console.error('Error reviewing restaurant:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderDocumentStatus = (doc, docType) => {
    if (!doc || !doc.documentUrl) {
      return <span style={{ color: 'var(--error)' }}>Not Uploaded</span>;
    }

    // Document URL is already a relative path (/uploads/documents/...)
    // Nginx proxies /uploads to backend, so we can use it directly
    const documentUrl = doc.documentUrl.startsWith('http') ? doc.documentUrl : doc.documentUrl;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
        <a
          href={documentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: 'var(--space-xs) var(--space-md)',
            background: 'var(--primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            textDecoration: 'none',
            fontSize: 'var(--font-size-sm)'
          }}
        >
          View Document
        </a>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={doc.verified || false}
            onChange={(e) => handleVerifyDocument(docType, e.target.checked)}
            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
          />
          <span style={{ color: doc.verified ? 'var(--success)' : 'var(--gray-600)' }}>
            {doc.verified ? 'Verified' : 'Not Verified'}
          </span>
        </label>
      </div>
    );
  };

  const allDocumentsVerified = selectedRestaurant &&
    selectedRestaurant.documents?.pan?.verified &&
    (selectedRestaurant.documents?.gst?.exempt || selectedRestaurant.documents?.gst?.verified) &&
    selectedRestaurant.documents?.fssai?.verified &&
    selectedRestaurant.documents?.bankAccount;

  return (
    <>
      <Navbar />
      <div style={{ padding: 'var(--space-2xl)', maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: 'var(--space-2xl)', color: 'var(--gray-900)' }}>
          Restaurant Document Reviews
        </h1>

      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--error-light)',
          color: 'var(--error)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-xl)'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))', gap: 'var(--space-xl)' }}>
        {/* Pending Restaurants List */}
        <div>
          <h2 style={{ marginBottom: 'var(--space-lg)', fontSize: 'var(--font-size-xl)' }}>
            Pending Reviews ({pendingRestaurants.length})
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {loading && !selectedRestaurant && <p>Loading...</p>}

            {pendingRestaurants.map((restaurant) => (
              <div
                key={restaurant._id}
                onClick={() => loadRestaurantDetails(restaurant._id)}
                style={{
                  padding: 'var(--space-lg)',
                  background: selectedRestaurant?._id === restaurant._id ? 'var(--primary-light)' : 'white',
                  border: `2px solid ${selectedRestaurant?._id === restaurant._id ? 'var(--primary)' : 'var(--gray-200)'}`,
                  borderRadius: 'var(--radius-lg)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)'
                }}
              >
                <h3 style={{ marginBottom: 'var(--space-sm)', color: 'var(--gray-900)' }}>
                  {restaurant.name}
                </h3>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                  Owner: {restaurant.ownerId?.name || 'N/A'}
                </p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                  Phone: {restaurant.phone}
                </p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                  Submitted: {new Date(restaurant.updatedAt).toLocaleDateString()}
                </p>
              </div>
            ))}

            {!loading && pendingRestaurants.length === 0 && (
              <p style={{ textAlign: 'center', color: 'var(--gray-600)', padding: 'var(--space-2xl)' }}>
                No pending restaurant reviews
              </p>
            )}
          </div>
        </div>

        {/* Restaurant Details Panel */}
        <div>
          {selectedRestaurant ? (
            <div style={{
              padding: 'var(--space-2xl)',
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--gray-200)'
            }}>
              <h2 style={{ marginBottom: 'var(--space-xl)', color: 'var(--gray-900)' }}>
                {selectedRestaurant.name}
              </h2>

              {/* Basic Info */}
              <div style={{ marginBottom: 'var(--space-2xl)' }}>
                <h3 style={{ marginBottom: 'var(--space-lg)', color: 'var(--gray-800)' }}>
                  Basic Information
                </h3>
                <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
                  <p><strong>Owner:</strong> {selectedRestaurant.ownerId?.name}</p>
                  <p><strong>Email:</strong> {selectedRestaurant.ownerId?.email}</p>
                  <p><strong>Phone:</strong> {selectedRestaurant.phone}</p>
                  <p><strong>Address:</strong> {selectedRestaurant.address?.street}, {selectedRestaurant.address?.city}, {selectedRestaurant.address?.state} - {selectedRestaurant.address?.zipCode}</p>
                  <p><strong>Cuisine Types:</strong> {selectedRestaurant.cuisineType?.join(', ') || 'N/A'}</p>
                  <p><strong>Device Type:</strong> {selectedRestaurant.deviceType?.replace('_', ' ').toUpperCase()}</p>
                </div>
              </div>

              {/* Restaurant Profile Photos */}
              <div style={{ marginBottom: 'var(--space-2xl)' }}>
                <h3 style={{ marginBottom: 'var(--space-lg)', color: 'var(--gray-800)' }}>
                  📸 Restaurant Profile Photos
                </h3>
                {selectedRestaurant.photos && selectedRestaurant.photos.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 'var(--space-md)'
                  }}>
                    {selectedRestaurant.photos.map((photo, index) => (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          borderRadius: 'var(--radius-lg)',
                          overflow: 'hidden',
                          aspectRatio: '16/9',
                          background: 'var(--gray-100)',
                          boxShadow: 'var(--shadow-sm)',
                          cursor: 'pointer'
                        }}
                        onClick={() => window.open(photo, '_blank')}
                      >
                        <img
                          src={photo}
                          alt={`Restaurant ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: 'var(--space-xl)',
                    background: 'var(--gray-50)',
                    borderRadius: 'var(--radius-lg)',
                    border: '2px dashed var(--gray-300)',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: 'var(--gray-600)', margin: 0 }}>
                      No profile photos uploaded
                    </p>
                  </div>
                )}
              </div>

              {/* Delivery Menu Images */}
              <div style={{ marginBottom: 'var(--space-2xl)' }}>
                <h3 style={{ marginBottom: 'var(--space-lg)', color: 'var(--gray-800)' }}>
                  🍽️ Delivery Menu Images
                </h3>
                {selectedRestaurant.menuImages && selectedRestaurant.menuImages.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 'var(--space-md)'
                  }}>
                    {selectedRestaurant.menuImages.map((menuImg, index) => (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          borderRadius: 'var(--radius-lg)',
                          overflow: 'hidden',
                          aspectRatio: '16/9',
                          background: 'var(--gray-100)',
                          boxShadow: 'var(--shadow-sm)',
                          cursor: 'pointer'
                        }}
                        onClick={() => window.open(menuImg, '_blank')}
                      >
                        <img
                          src={menuImg}
                          alt={`Menu ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: 'var(--space-xl)',
                    background: 'var(--gray-50)',
                    borderRadius: 'var(--radius-lg)',
                    border: '2px dashed var(--gray-300)',
                    textAlign: 'center'
                  }}>
                    <p style={{ color: 'var(--gray-600)', margin: 0 }}>
                      No menu images uploaded
                    </p>
                  </div>
                )}
              </div>

              {/* Documents */}
              <div style={{ marginBottom: 'var(--space-2xl)' }}>
                <h3 style={{ marginBottom: 'var(--space-lg)', color: 'var(--gray-800)' }}>
                  Documents Verification
                </h3>

                {/* PAN */}
                <div style={{
                  padding: 'var(--space-lg)',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-md)'
                }}>
                  <h4 style={{ marginBottom: 'var(--space-md)' }}>PAN Card</h4>
                  <p><strong>Number:</strong> {selectedRestaurant.documents?.pan?.number || 'N/A'}</p>
                  <p style={{ marginBottom: 'var(--space-md)' }}><strong>Holder Name:</strong> {selectedRestaurant.documents?.pan?.holderName || 'N/A'}</p>
                  {renderDocumentStatus(selectedRestaurant.documents?.pan, 'pan')}
                </div>

                {/* GST */}
                <div style={{
                  padding: 'var(--space-lg)',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-md)'
                }}>
                  <h4 style={{ marginBottom: 'var(--space-md)' }}>GST Certificate</h4>
                  {selectedRestaurant.documents?.gst?.exempt ? (
                    <>
                      <div style={{ padding: '10px 14px', background: '#fef3c7', borderRadius: '8px', marginBottom: '12px', border: '1px solid #f59e0b' }}>
                        <strong style={{ color: '#92400e' }}>GST Exempt (Declaration Filed)</strong>
                        <p style={{ fontSize: '13px', color: '#92400e', margin: '4px 0 0' }}>
                          Under Section 9(5) CGST Act — turnover below threshold
                        </p>
                      </div>
                      <p><strong>Owner:</strong> {selectedRestaurant.documents?.gst?.exemptDeclaration?.ownerName || 'N/A'}</p>
                      <p><strong>Business:</strong> {selectedRestaurant.documents?.gst?.exemptDeclaration?.businessName || 'N/A'}</p>
                      <p style={{ marginBottom: 'var(--space-md)' }}><strong>Address:</strong> {selectedRestaurant.documents?.gst?.exemptDeclaration?.businessAddress || 'N/A'}</p>
                    </>
                  ) : (
                    <>
                      <p><strong>Number:</strong> {selectedRestaurant.documents?.gst?.number || 'N/A'}</p>
                      <p style={{ marginBottom: 'var(--space-md)' }}><strong>Business Name:</strong> {selectedRestaurant.documents?.gst?.businessName || 'N/A'}</p>
                    </>
                  )}
                  {renderDocumentStatus(selectedRestaurant.documents?.gst, 'gst')}
                </div>

                {/* FSSAI */}
                <div style={{
                  padding: 'var(--space-lg)',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-md)'
                }}>
                  <h4 style={{ marginBottom: 'var(--space-md)' }}>FSSAI License</h4>
                  <p><strong>Number:</strong> {selectedRestaurant.documents?.fssai?.number || 'N/A'}</p>
                  <p><strong>License Name:</strong> {selectedRestaurant.documents?.fssai?.licenseName || 'N/A'}</p>
                  <p><strong>Address:</strong> {selectedRestaurant.documents?.fssai?.address || 'N/A'}</p>
                  <p style={{ marginBottom: 'var(--space-md)' }}><strong>Expiry Date:</strong> {selectedRestaurant.documents?.fssai?.expiryDate ? new Date(selectedRestaurant.documents.fssai.expiryDate).toLocaleDateString() : 'N/A'}</p>
                  {renderDocumentStatus(selectedRestaurant.documents?.fssai, 'fssai')}

                  {/* Validation Warnings */}
                  <div style={{ marginTop: 'var(--space-md)', padding: 'var(--space-md)', background: 'var(--warning-light)', borderRadius: 'var(--radius-sm)' }}>
                    <p style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-xs)' }}>
                      <strong>Validation Check:</strong>
                    </p>
                    <p style={{ fontSize: 'var(--font-size-sm)' }}>
                      - FSSAI address should match restaurant address<br />
                      - FSSAI license name should match PAN holder name or restaurant name
                    </p>
                  </div>
                </div>

                {/* Bank Account */}
                <div style={{
                  padding: 'var(--space-lg)',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-md)'
                }}>
                  <h4 style={{ marginBottom: 'var(--space-md)' }}>Bank Account Details</h4>
                  <p><strong>Account Number:</strong> {selectedRestaurant.documents?.bankAccount?.accountNumber || 'N/A'}</p>
                  <p><strong>Account Holder:</strong> {selectedRestaurant.documents?.bankAccount?.accountHolderName || 'N/A'}</p>
                  <p><strong>IFSC Code:</strong> {selectedRestaurant.documents?.bankAccount?.ifscCode || 'N/A'}</p>
                  <p><strong>Bank Name:</strong> {selectedRestaurant.documents?.bankAccount?.bankName || 'N/A'}</p>
                  <p><strong>Branch:</strong> {selectedRestaurant.documents?.bankAccount?.branchName || 'N/A'}</p>
                </div>
              </div>

              {/* Rejection Reason Input - Always visible */}
              <div style={{ marginBottom: 'var(--space-xl)' }}>
                <label style={{ display: 'block', marginBottom: 'var(--space-sm)', fontWeight: '600' }}>
                  Rejection Reason
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection (required if rejecting)..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: 'var(--space-md)',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-base)',
                    fontFamily: 'inherit'
                  }}
                />
                <p style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--gray-600)',
                  marginTop: 'var(--space-xs)',
                  fontStyle: 'italic'
                }}>
                  Provide a detailed reason if you need to reject this restaurant application
                </p>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: 'var(--space-md)',
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
                marginTop: 'var(--space-xl)'
              }}>
                <button
                  onClick={() => handleReviewRestaurant(false)}
                  disabled={loading}
                  style={{
                    padding: 'var(--space-md) var(--space-xl)',
                    background: 'var(--error)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    minWidth: '180px',
                    display: 'inline-block'
                  }}
                >
                  ❌ Reject Restaurant
                </button>
                <button
                  onClick={() => handleReviewRestaurant(true)}
                  disabled={loading || !allDocumentsVerified}
                  style={{
                    padding: 'var(--space-md) var(--space-xl)',
                    background: allDocumentsVerified ? 'var(--success)' : 'var(--gray-400)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: '600',
                    cursor: (loading || !allDocumentsVerified) ? 'not-allowed' : 'pointer',
                    opacity: (loading || !allDocumentsVerified) ? 0.6 : 1,
                    minWidth: '180px',
                    display: 'inline-block'
                  }}
                >
                  {allDocumentsVerified ? '✅ Approve Restaurant' : '⚠️ Verify Documents First'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              padding: 'var(--space-2xl)',
              background: 'var(--gray-50)',
              borderRadius: 'var(--radius-lg)',
              textAlign: 'center',
              color: 'var(--gray-600)'
            }}>
              <p>Select a restaurant from the list to review documents</p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default RestaurantReviews;
